/**
 * The spine of the onboarding experience: where you are, what is done, what is left, and what
 * can be skipped. Sections are clickable so nobody is trapped in a linear questionnaire.
 */

import type { SectionProgress } from "../lib/surveyProgress";

export interface SectionNavigatorProps {
  sections: SectionProgress[];
  currentKey: string | undefined;
  onSelect: (key: string) => void;
}

function statusOf(section: SectionProgress, isCurrent: boolean) {
  if (section.complete) return { label: "Done", tone: "verified" as const };
  if (isCurrent) return { label: "Here", tone: "accent" as const };
  if (section.started) return { label: "Started", tone: "open" as const };
  if (section.optional) return { label: "Optional", tone: "neutral" as const };
  return { label: "To do", tone: "neutral" as const };
}

export function SectionNavigator({ sections, currentKey, onSelect }: SectionNavigatorProps) {
  const done = sections.filter((section) => section.complete).length;

  return (
    <nav className="section-nav" aria-label="Onboarding sections">
      <div className="section-nav__header">
        <strong>Sections</strong>
        <span className="tiny faint">
          {done} of {sections.length} done
        </span>
      </div>

      <ol className="section-nav__list">
        {sections.map((section) => {
          const isCurrent = section.key === currentKey;
          const status = statusOf(section, isCurrent);
          const answered = section.answeredCount;
          const total = section.questionCount;
          return (
            <li key={section.key}>
              <button
                type="button"
                className={`section-nav__item${isCurrent ? " is-current" : ""}${
                  section.complete ? " is-complete" : ""
                }`}
                onClick={() => onSelect(section.key)}
                aria-current={isCurrent ? "step" : undefined}
              >
                <span className="section-nav__label">
                  {section.label}
                  {section.optional ? (
                    <span className="tiny faint"> · can skip</span>
                  ) : null}
                </span>
                <span className="section-nav__meta">
                  <span className={`badge badge--${status.tone}`}>{status.label}</span>
                  <span className="tiny faint nowrap">
                    {answered}/{total}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
